
import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useDroppable } from '@dnd-kit/core';
import TaskItemCard from './TaskItemCard';
import { ITask } from '../../../../interfaces/ITasks';

interface TaskColumnProps {
    id: string;
    tasks: ITask[];
    onTaskClick: (task: ITask) => void;
    onAddTask: () => void;
    isActiveDropTarget?: boolean;
}

const TaskColumn: React.FC<TaskColumnProps> = ({
    id,
    tasks,
    onTaskClick,
    onAddTask,
    isActiveDropTarget = false,
}) => {
    const { isOver, setNodeRef } = useDroppable({
        id,
    });

    const shouldHighlight = isOver || isActiveDropTarget;

    return (
        <Box
            ref={setNodeRef}
            data-task-column-id={id}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 350,
                minWidth: 260,
                bgcolor: shouldHighlight ? 'action.hover' : 'transparent',
                borderRadius: 1,
                transition: 'background-color 0.2s ease',
                border: shouldHighlight ? '2px dashed' : 'none',
                borderColor: 'primary.main',
                p: shouldHighlight ? 1 : 0,
            }}
        >

            {/* Add Task Button (Fixed Top) */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 60,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                    },
                }}
                onClick={onAddTask}
            >
                <IconButton
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                            backgroundColor: 'transparent',
                        },
                    }}
                >
                    <AddIcon />
                </IconButton>

                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        ml: 0.5,
                        fontSize: '0.75rem',
                    }}
                >
                    Add task
                </Typography>
            </Box>

            {/* Scrollable Tasks */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                }}
            >
                {tasks.map((task) => (
                    <Box key={task.id} data-task-id={task.id}>
                        <TaskItemCard
                            task={task}
                            onClick={() => onTaskClick(task)}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default TaskColumn;
